/**
 * Triage helpers — pure derivations over `VulnerabilityEventRow`.
 *
 * Kept out of the page component so the queue table and both dialogs agree on
 * every label, score and customer roll-up. Nothing here does I/O, so all of it
 * stays cheap enough to run inside a `useMemo` on every keystroke of the filter.
 *
 * Two contract details this module exists to encapsulate:
 *   - `CveFacts.base_score` is a CMDBuild "double" that arrives as a STRING when
 *     the CSV import wrote it, so no call site may compare it numerically
 *     without going through `parseScore` first.
 *   - `KevFacts` is null-per-field when the CVE is not on the KEV list *or* when
 *     the native assertion lookup failed. A missing KEV date is therefore never an error
 *     and must not be rendered as one.
 */

import { msg, t } from '@lingui/core/macro';

import type { ChipTone } from '../../components/Chip';
import { cvssTone } from '../../components/CvssScore';
import { intlLocale, translatedRecord } from '../../lib/locale';
import type {
  AssessmentPriority,
  AssessmentStatus,
  BenachrichtigungStatus,
  Bewertung,
  EmailDraft,
  EventKunde,
  EventProduktVersion,
  VexJustification,
  VulnerabilityEventRow,
} from '../../lib/types';

/** Customer notification states; QUEUED, SENDING and FAILED are the Postausgang (DEV-93). */
export const NOTIFICATION_STATUS_LABELS = translatedRecord<BenachrichtigungStatus>({
  OFFEN: msg`Offen`,
  QUEUED: msg`Eingereiht`,
  SENDING: msg`In Versand`,
  FAILED: msg`Fehlgeschlagen`,
  VERSENDET: msg`Versendet`,
  GEMELDET: msg`Gemeldet`,
  ABGESCHLOSSEN: msg`Abgeschlossen`,
});

const SENT_NOTIFICATION_STATES: ReadonlySet<BenachrichtigungStatus> = new Set(['VERSENDET', 'GEMELDET', 'ABGESCHLOSSEN']);

/** Only a mail that went out counts as notified — a queued one does not. */
export function isNotificationSent(status: BenachrichtigungStatus): boolean {
  return SENT_NOTIFICATION_STATES.has(status);
}

export function notificationTone(status: BenachrichtigungStatus): ChipTone {
  if (isNotificationSent(status)) return 'good';
  return status === 'FAILED' ? 'alert' : 'warn';
}

/** Labels of the component lookup (BEHOBEN: closed by the KEV scan). */
export const BEWERTUNG_LABELS = translatedRecord<Bewertung>({
  OFFEN: msg`Offen`,
  UNDER_INVESTIGATION: msg`In Prüfung`,
  BETROFFEN: msg`Betroffen`,
  NICHT_BETROFFEN: msg`Nicht betroffen`,
  BEHOBEN: msg`Vom Scan geschlossen`,
});

/** Chip tone per verdict — open work is amber, an applicable CVE is red. */
export const BEWERTUNG_TONE: Readonly<Record<Bewertung, ChipTone>> = {
  OFFEN: 'warn',
  UNDER_INVESTIGATION: 'info',
  BETROFFEN: 'alert',
  NICHT_BETROFFEN: 'good',
  BEHOBEN: 'idle',
};

/* Bottom-up assessment (DEV-100) — one vocabulary on all three levels. */

export const STATUS_LABELS = translatedRecord<AssessmentStatus>({
  OPEN: msg`Offen`,
  UNDER_INVESTIGATION: msg`In Prüfung`,
  AFFECTED: msg`Betroffen`,
  NOT_AFFECTED: msg`Nicht betroffen`,
  FIXED: msg`Behoben`,
});

export const STATUS_TONE: Readonly<Record<AssessmentStatus, ChipTone>> = {
  OPEN: 'warn',
  UNDER_INVESTIGATION: 'info',
  AFFECTED: 'alert',
  NOT_AFFECTED: 'good',
  FIXED: 'good',
};

/** VEX justification catalog in the order of the CSAF specification. */
export const JUSTIFICATION_LABELS = translatedRecord<VexJustification>({
  COMPONENT_NOT_PRESENT: msg`Komponente nicht vorhanden`,
  VULNERABLE_CODE_NOT_PRESENT: msg`Verwundbarer Code nicht vorhanden`,
  VULNERABLE_CODE_NOT_IN_EXECUTE_PATH: msg`Nicht im Ausführungspfad`,
  VULNERABLE_CODE_CANNOT_BE_CONTROLLED_BY_ADVERSARY: msg`Nicht durch Angreifer steuerbar`,
  INLINE_MITIGATIONS_ALREADY_EXIST: msg`Mitigation bereits integriert`,
  ENVIRONMENT: msg`Durch die Einsatzumgebung ausgeschlossen`,
});

export const PRIORITY_LABELS = translatedRecord<AssessmentPriority>({
  HIGH: msg`Hoch`,
  MEDIUM: msg`Mittel`,
  LOW: msg`Niedrig`,
});

export const PRIORITY_TONE: Readonly<Record<AssessmentPriority, ChipTone>> = {
  HIGH: 'alert',
  MEDIUM: 'warn',
  LOW: 'neutral',
};

/** The component lookup (German codes) in the shared vocabulary. */
export function componentStatus(bewertung: Bewertung): AssessmentStatus {
  switch (bewertung) {
    case 'BETROFFEN': return 'AFFECTED';
    case 'NICHT_BETROFFEN': return 'NOT_AFFECTED';
    case 'UNDER_INVESTIGATION': return 'UNDER_INVESTIGATION';
    default: return 'OPEN';
  }
}

/** A verdict (not just open or in progress) — deviating from it needs a reason. */
export function isVerdict(status: AssessmentStatus): boolean {
  return status === 'AFFECTED' || status === 'NOT_AFFECTED' || status === 'FIXED';
}

/**
 * Client-side mirror of the server rules, so the save button can say why it is
 * disabled. The server stays the authority (422 with the same German message).
 */
export function verdictProblem(
  level: 'component' | 'product' | 'instance',
  status: AssessmentStatus,
  justification: VexJustification | null,
  comment: string,
  proposal: AssessmentStatus,
): string | null {
  if (justification === 'ENVIRONMENT' && level !== 'instance') {
    return t`Umgebungsbezogene Begründungen sind nur bei der Instanzbewertung zulässig`;
  }
  if (status === 'NOT_AFFECTED') {
    if (justification === null) return t`„Nicht betroffen“ erfordert eine VEX-Begründung`;
    if (comment.trim() === '') return t`„Nicht betroffen“ erfordert eine Erläuterung`;
  }
  if (level !== 'component' && isVerdict(proposal) && status !== proposal && comment.trim() === '') {
    const proposed = STATUS_LABELS[proposal];
    return t`Abweichung vom Vorschlag „${proposed}“ erfordert eine Begründung`;
  }
  return null;
}

/**
 * Numeric CVSS base score, or null when the value is absent/unparsable.
 * Accepts the German decimal comma because CMDBuild text attributes have been
 * seen carrying '9,8' after a locale-formatted CSV export.
 */
export function parseScore(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = value.trim().replace(',', '.');
  if (text === '') return null;
  const parsed = Number.parseFloat(text);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Chip tone for a CVSS score, on the severity scale (see CvssScore). */
export function scoreTone(score: number | null): ChipTone {
  return cvssTone(score);
}

/** 'Release 3.2' / 'V3.2' / '(unbenannt)' — never an empty cell. */
export function produktVersionLabel(version: EventProduktVersion): string {
  const name = version.name?.trim();
  if (name) return name;
  const nr = version.version_nr?.trim();
  if (nr) return `V${nr}`;
  return t`(unbenannte Produktversion)`;
}

/** 'Firmware 2.1 · Siemens' — component identity in one line. */
export function komponenteLabel(event: VulnerabilityEventRow): string {
  const parts = [event.komponente.name, event.komponente.version, event.komponente.hersteller]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : t`(unbekannte Komponentenversion)`;
}

/**
 * Distinct customers behind the affected instances, sorted by name.
 * Instances whose Kunde is entirely unknown are skipped — the count of affected
 * instances is reported separately, so nothing gets lost by dropping them here.
 */
export function kundenOf(event: VulnerabilityEventRow): EventKunde[] {
  const seen = new Set<string>();
  const out: EventKunde[] = [];
  for (const instanz of event.betroffene) {
    const kunde = instanz.kunde;
    if (kunde.id === null && !kunde.name) continue;
    const key = kunde.id !== null ? `id:${kunde.id}` : `name:${kunde.name ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(kunde);
  }
  out.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', intlLocale()));
  return out;
}

/**
 * Stable key of a draft: its Benachrichtigung (DEV-128). A customer can have
 * a sent notification and a follow-up side by side, so the customer id is not
 * unique.
 */
export function draftKey(draft: EmailDraft): number {
  return draft.benachrichtigung.id ?? -draft.kunde.id;
}

/**
 * Client-side substring filter over everything a triage operator would type:
 * CVE id and text, KEV name, component, product versions, customers, serial
 * numbers and the audit trail.
 */
export function matchesSearch(event: VulnerabilityEventRow, needle: string): boolean {
  const query = needle.trim().toLowerCase();
  if (query === '') return true;
  const haystack: (string | null)[] = [
    event.cve.id,
    event.cve.beschreibung,
    event.kev.name,
    event.kev.beschreibung,
    event.komponente.name,
    event.komponente.version,
    event.komponente.hersteller,
    event.begruendung,
    event.bewertet_von,
    ...event.produktversionen.map(produktVersionLabel),
    ...event.betroffene.flatMap((instanz) => [
      instanz.seriennummer,
      instanz.produkttyp,
      instanz.produktversion,
      instanz.kunde.name,
      instanz.kunde.email,
    ]),
  ];
  return haystack.some((value) => value !== null && value.toLowerCase().includes(query));
}
